// Thin API client for the developer console. Access token lives in memory;
// the refresh token is an httpOnly cookie sent to /api/portal/auth/refresh
// automatically. On a 401 we transparently refresh once and retry.

export interface Account {
  id: string
  email: string
  name: string
  plan: string
  createdAt: string
}

// An API key. Each key is a bot: a name, a description, and a token that
// authenticates against the svoisozvon bot API.
export interface ApiKey {
  id: string
  name: string
  description: string | null
  token: string
  botUsername: string | null
  status: 'active' | 'revoked'
  createdAt: string
  lastUsedAt: string | null
  revokedAt: string | null
}

let accessToken: string | null = null

export function setToken(t: string | null) { accessToken = t }
export function getToken() { return accessToken }

class ApiError extends Error {
  status: number
  constructor(status: number, message: string) { super(message); this.status = status }
}

async function refresh(): Promise<boolean> {
  try {
    const res = await fetch('/api/portal/auth/refresh', { method: 'POST', credentials: 'include' })
    if (!res.ok) return false
    const data = await res.json()
    accessToken = data.accessToken
    return true
  } catch { return false }
}

async function raw(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const headers = new Headers(init.headers)
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)
  const res = await fetch(path, { ...init, headers, credentials: 'include' })
  if (res.status === 401 && retry && accessToken) {
    if (await refresh()) return raw(path, init, false)
  }
  return res
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  const res = await raw(path, { ...init, headers })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApiError(res.status, (data as any).error || `Error ${res.status}`)
  return data as T
}

export const api = {
  // ── auth ──
  // Step 1: email a verification code, get back a sessionKey.
  requestCode: (email: string) =>
    json<{ ok: true; sessionKey: string }>('/api/portal/auth/request-code', {
      method: 'POST', body: JSON.stringify({ email }),
    }),
  // Step 2: verify the code and create the account.
  register: (email: string, password: string, name: string, sessionKey: string, code: string) =>
    json<{ accessToken: string; account: Account }>('/api/portal/auth/register', {
      method: 'POST', body: JSON.stringify({ email, password, name, sessionKey, code }),
    }),
  login: (email: string, password: string) =>
    json<{ accessToken: string; account: Account }>('/api/portal/auth/login', {
      method: 'POST', body: JSON.stringify({ email, password }),
    }),
  refresh,
  logout: () => json('/api/portal/auth/logout', { method: 'POST' }),

  // ── keys (bots) ──
  me: () => json<{ account: Account; limits: { maxKeys: number }; keys: ApiKey[] }>('/api/portal/me'),
  listKeys: () => json<{ keys: ApiKey[] }>('/api/portal/keys'),
  createKey: (name: string, description: string) =>
    json<{ key: ApiKey }>('/api/portal/keys', { method: 'POST', body: JSON.stringify({ name, description }) }),
  revokeKey: (id: string) => json(`/api/portal/keys/${id}/revoke`, { method: 'POST' }),
  deleteKey: (id: string) => json(`/api/portal/keys/${id}`, { method: 'DELETE' }),
}

export { ApiError }
